import { useCallback, useEffect, useState } from "react";
import { Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, fmtDate } from "../lib/api";
import { ConfirmCodeDialog } from "../components/ConfirmCodeDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copiado"); };

const Webhook = () => {
  const api = useApi();
  const [w, setW] = useState({ url: "", secret: "", deliveries: [] });
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/admin/webhook").then((r) => { setW(r.data); setUrl(r.data.url); }).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (code) => {
    try { await api.put("/admin/webhook", { url, code }); toast.success("Webhook salvo"); setOpen(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try {
      const { data } = await api.post("/admin/webhook/test");
      toast[data.status_code >= 200 && data.status_code < 300 ? "success" : "error"](`Resposta: ${data.status_code || "sem resposta"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="card space-y-4" data-testid="webhook-section">
      <h2 className="font-heading text-lg font-bold">Webhook da sua plataforma</h2>
      <p className="text-sm text-slate-400">Enviamos eventos <code>deposit.paid</code>, <code>payout.paid</code>, <code>payout.rejected</code> com assinatura HMAC-SHA256.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input className="field" placeholder="https://suaplataforma.com/webhooks/gateway" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="webhook-url-input" />
        <Button className="btn-primary" onClick={() => setOpen(true)} data-testid="save-webhook-url-button">Salvar</Button>
        <Button className="btn-ghost" disabled={!w.url} onClick={test} data-testid="test-webhook-button"><Send size={14} className="mr-1" /> Testar</Button>
      </div>
      {w.secret && (
        <div><Label className="caption">Segredo de assinatura</Label>
          <div className="code-block mt-1 flex items-center gap-2"><span className="flex-1 truncate" data-testid="webhook-secret-text">{w.secret}</span><Button size="sm" variant="ghost" onClick={() => copy(w.secret)} data-testid="copy-webhook-secret-button"><Copy size={14} /></Button></div>
        </div>
      )}
      {w.deliveries.length > 0 && (
        <div className="text-xs text-slate-400" data-testid="webhook-deliveries">
          {w.deliveries.map((d) => <div key={d.id} className="flex justify-between border-b border-slate-800 py-1.5"><span>{d.event}</span><span>{d.status_code || "falha"} · {fmtDate(d.created_at)}</span></div>)}
        </div>
      )}
      <ConfirmCodeDialog open={open} onOpenChange={setOpen} title="Salvar webhook" description="Alterações no webhook exigem o código 2FA." onConfirm={save} />
    </div>
  );
};

export default function ApiKeys() {
  const api = useApi();
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [ips, setIps] = useState("");
  const [dialog, setDialog] = useState(null);
  const [created, setCreated] = useState(null);
  const load = useCallback(() => { api.get("/admin/api-keys").then((r) => setKeys(r.data)).catch(() => {}); }, [api]);
  useEffect(() => { load(); }, [load]);

  const confirm = async (code) => {
    try {
      if (dialog.kind === "create") {
        const { data } = await api.post("/admin/api-keys", { name, code, ip_allowlist: ips.split(",").map((s) => s.trim()).filter(Boolean) });
        setCreated(data.secret_key); setName(""); setIps("");
      } else {
        await api.post(`/admin/api-keys/${dialog.key.id}/revoke`, { code });
        toast.success("Chave revogada");
      }
      setDialog(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-8 fade-up">
      <div><p className="caption">Integração</p><h1 className="font-heading text-4xl font-extrabold sm:text-5xl">API e webhooks</h1></div>
      <form className="card space-y-4" onSubmit={(e) => { e.preventDefault(); setDialog({ kind: "create" }); }} data-testid="new-api-key-form">
        <h2 className="font-heading text-lg font-bold">Nova chave de API</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="caption">Nome</Label><Input className="field mt-1" required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder="Minha plataforma" data-testid="api-key-name-input" /></div>
          <div><Label className="caption">IPs permitidos (opcional, separados por vírgula)</Label><Input className="field mt-1" value={ips} onChange={(e) => setIps(e.target.value)} placeholder="203.0.113.10" data-testid="api-key-ips-input" /></div>
        </div>
        <Button type="submit" className="btn-primary" data-testid="generate-api-key-button">Gerar chave</Button>
        {created && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4" data-testid="api-key-created-box">
            <p className="text-sm font-semibold text-amber-400">Copie agora. Por segurança, esta chave não será exibida novamente.</p>
            <div className="code-block mt-2 flex items-center gap-2"><span className="flex-1 break-all" data-testid="api-key-secret-text">{created}</span><Button type="button" size="sm" variant="ghost" onClick={() => copy(created)} data-testid="copy-api-key-button"><Copy size={14} /></Button></div>
          </div>
        )}
      </form>
      <div className="card">
        <h2 className="mb-4 font-heading text-lg font-bold">Chaves</h2>
        <div className="overflow-x-auto">
          <table className="tx-table" data-testid="api-keys-table">
            <thead><tr><th>Nome</th><th>Prefixo</th><th>IPs</th><th>Último uso</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {keys.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">Nenhuma chave criada.</td></tr>}
              {keys.map((k) => (
                <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                  <td>{k.name}</td><td className="font-mono text-xs">{k.prefix}…</td>
                  <td className="text-xs text-slate-400">{k.ip_allowlist?.join(", ") || "Qualquer"}</td>
                  <td className="text-xs text-slate-400">{fmtDate(k.last_used_at)}</td>
                  <td>{k.revoked ? <span className="text-xs text-rose-400">Revogada</span> : <span className="text-xs text-emerald-400">Ativa</span>}</td>
                  <td className="text-right">{!k.revoked && <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => setDialog({ kind: "revoke", key: k })} data-testid={`revoke-api-key-${k.id}`}>Revogar</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Webhook />
      <ConfirmCodeDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)} title={dialog?.kind === "revoke" ? "Revogar chave" : "Gerar chave de API"} description="Ação sensível: confirme com o Google Authenticator." onConfirm={confirm} />
    </div>
  );
}
